import React from 'react';
import { inventoryService } from '../../../services/inventoryService';
import type { FinishedGoodsProductionRequest } from '../../../services/inventory/inventoryMappers';
import { useLanguage } from '../../../features/language/useLanguage';

export type WasteAction = 'keep' | 'waste';

export type WasteRow = {
  itemId: string;
  roastType: string;
  itemName: string;
  itemCode?: string;
  systemRemainingKg: number;
  remainingKg: number;
  remainingInput: string;
  action: WasteAction;
};

const buildWasteRows = async (request: FinishedGoodsProductionRequest): Promise<WasteRow[]> => {
  const componentKeys = new Map<string, { item_id: string; roast_type: string }>();
  request.components.forEach((component) => {
    componentKeys.set(`${component.item_id}:${component.roast_type}`, component);
  });
  const uniqueComponents = Array.from(componentKeys.values());

  let groups: Awaited<ReturnType<typeof inventoryService.getRoastedBulkGroups>> = [];
  try {
    groups = await inventoryService.getRoastedBulkGroups(true);
  } catch {
    groups = [];
  }

  return uniqueComponents.map((component) => {
    const group = groups.find((g) => g.itemId === component.item_id && g.roastType === component.roast_type);
    const remainingKg = Number(group?.totalStock || 0);
    return {
      itemId: component.item_id,
      roastType: component.roast_type,
      itemName: group?.itemName || component.item_id,
      itemCode: group?.itemCode,
      systemRemainingKg: remainingKg,
      remainingKg,
      remainingInput: remainingKg > 0 ? remainingKg.toFixed(3) : '0.000',
      action: 'keep',
    };
  });
};

const sortFifo = <T extends { expiryDate?: string | null; workOrderNumber?: string | null; lotId: string }>(
  items: T[]
): T[] =>
  [...items].sort((a, b) => {
    const aExp = a.expiryDate ? new Date(a.expiryDate).getTime() : Number.POSITIVE_INFINITY;
    const bExp = b.expiryDate ? new Date(b.expiryDate).getTime() : Number.POSITIVE_INFINITY;
    if (aExp !== bExp) return aExp - bExp;
    const aNo = a.workOrderNumber || '';
    const bNo = b.workOrderNumber || '';
    if (aNo !== bNo) return aNo.localeCompare(bNo);
    return a.lotId.localeCompare(b.lotId);
  });

export const useWasteManagement = () => {
  const { t } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const [rows, setRows] = React.useState<WasteRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [adjustConfirmOpen, setAdjustConfirmOpen] = React.useState(false);
  const [editingKey, setEditingKey] = React.useState<string | null>(null);

  const openWaste = React.useCallback(async (request: FinishedGoodsProductionRequest) => {
    setError(null);
    const built = await buildWasteRows(request);
    const hasRemaining = built.some((row) => Number(row.remainingKg || 0) > 0);
    if (!hasRemaining) {
      setRows([]);
      setEditingKey(null);
      setAdjustConfirmOpen(false);
      setOpen(false);
      return;
    }
    setRows(built);
    setEditingKey(null);
    setAdjustConfirmOpen(false);
    setOpen(true);
  }, []);

  const closeWaste = React.useCallback(() => {
    setOpen(false);
    setAdjustConfirmOpen(false);
    setEditingKey(null);
    setRows([]);
    setError(null);
  }, []);

  const closeAdjustConfirm = React.useCallback(() => {
    if (submitting) return;
    setAdjustConfirmOpen(false);
  }, [submitting]);

  const run = React.useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const keepTargets = rows.filter((row) => row.action === 'keep');
      const keepManualAdjustments = keepTargets
        .map((row) => {
          const delta = Number(row.remainingKg || 0) - Number(row.systemRemainingKg || 0);
          return { row, delta };
        })
        .filter(({ delta }) => Math.abs(delta) > 0.0005);

      for (const { row, delta } of keepManualAdjustments) {
        const groupItems = await inventoryService.getRoastedBulkGroupItems(row.itemId, row.roastType);
        const fifo = sortFifo(groupItems);

        if (fifo.length === 0) {
          throw new Error(t('workOrders.waste.errorNoLot', { itemName: row.itemName, roastType: row.roastType }));
        }

        const note = t('workOrders.waste.manualAdjustmentNote', {
          from: Number(row.systemRemainingKg || 0).toFixed(3),
          to: Number(row.remainingKg || 0).toFixed(3),
        });

        if (delta > 0) {
          await inventoryService.adjustStock(fifo[0].inventoryItemId, parseFloat(delta.toFixed(3)), 'add', note);
        } else {
          let remainingToRemove = Math.abs(delta);
          for (const item of fifo) {
            const qty = Number(item.remainingQuantity || 0);
            if (qty <= 0) continue;
            if (remainingToRemove <= 0.0005) break;
            const removeQty = Math.min(qty, remainingToRemove);
            await inventoryService.adjustStock(item.inventoryItemId, parseFloat(removeQty.toFixed(3)), 'remove', note);
            remainingToRemove -= removeQty;
          }
          if (remainingToRemove > 0.0005) {
            throw new Error(t('workOrders.waste.errorStockChanged', { itemName: row.itemName }));
          }
        }
      }

      const wasteTargets = rows.filter((row) => row.action === 'waste');
      for (const target of wasteTargets) {
        const groupItems = await inventoryService.getRoastedBulkGroupItems(target.itemId, target.roastType);
        const fifo = sortFifo(groupItems);

        for (const item of fifo) {
          const qty = Number(item.remainingQuantity || 0);
          if (qty <= 0) continue;
          await inventoryService.adjustStock(
            item.inventoryItemId,
            parseFloat(qty.toFixed(3)),
            'remove',
            t('workOrders.waste.wasteNote')
          );
        }
      }

      setOpen(false);
      setAdjustConfirmOpen(false);
      setRows([]);
    } catch (err: unknown) {
      if (err instanceof Error && err.message.trim() !== '') {
        setError(err.message);
        return;
      }
      const errorResponse = err as { response?: { data?: { error?: string; message?: string } } };
      const message =
        errorResponse?.response?.data?.error ||
        errorResponse?.response?.data?.message ||
        t('workOrders.waste.errorProcess');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }, [rows, t]);

  const handleConfirm = React.useCallback(async () => {
    const manualKeepTargets = rows.filter((row) => {
      if (row.action !== 'keep') return false;
      const delta = Number(row.remainingKg || 0) - Number(row.systemRemainingKg || 0);
      return Math.abs(delta) > 0.0005;
    });
    if (manualKeepTargets.length > 0) {
      setAdjustConfirmOpen(true);
      return;
    }
    await run();
  }, [rows, run]);

  return {
    open,
    rows,
    setRows,
    error,
    submitting,
    adjustConfirmOpen,
    closeAdjustConfirm,
    editingKey,
    setEditingKey,
    openWaste,
    closeWaste,
    run,
    handleConfirm,
  };
};
